import React from "react";
import { Link } from "react-router-dom";
import { BarChart3, Map as MapIcon, Megaphone, Building2, MapPin, Users, AlertOctagon, ExternalLink, ShieldCheck, Settings as SettingsIcon, Activity, Compass } from "lucide-react";

// Iter93 — "planning" (Planification) is admin-only by construction,
// not by hiding it here: AdminTabsBar only ever renders inside
// AdminPanelPage, which agency dispatchers never reach (they have a
// completely separate login + AgencyDashboardPage/AgencyMapPage, with
// their own `agency_sessions` cookie that `require_admin` rejects).
// The server-side gate lives in routers/planification.py
// (`GET /admin/planification/access`, `Depends(require_admin)`).
export const ADMIN_TABS = ["dashboard", "reports", "users", "alerts", "map", "planning", "areas", "broadcasts", "agencies", "audit", "settings", "system"];

// Short labels for browser tab titles (document.title) — kept terse so
// the OS task-switcher row stays readable.
export const ADMIN_TAB_TITLES = {
  dashboard: "Tableau",
  reports: "Rapports",
  users: "Utilisateurs",
  alerts: "Alertes",
  map: "Carte",
  planning: "Stratégie",
  areas: "Zones",
  broadcasts: "Diffusion",
  agencies: "Agences",
  audit: "Audit",
  settings: "Paramètres",
  system: "Système",
};

const tabIcon = (t) => {
  if (t === "dashboard") return BarChart3;
  if (t === "reports") return BarChart3;
  if (t === "users") return Users;
  if (t === "alerts") return AlertOctagon;
  if (t === "map") return MapIcon;
  if (t === "planning") return Compass;
  if (t === "areas") return MapPin;
  if (t === "broadcasts") return Megaphone;
  if (t === "agencies") return Building2;
  if (t === "audit") return ShieldCheck;
  if (t === "settings") return SettingsIcon;
  if (t === "system") return Activity;
  return null;
};

const tabLabel = (t, usersCount, usersHasMore, alertsCount, alertsHasMore) => {
  if (t === "dashboard") return "Tableau";
  if (t === "reports") return "Rapports";
  if (t === "users") return `Utilisateurs (${usersCount}${usersHasMore ? "+" : ""})`;
  if (t === "alerts") return `Alertes (${alertsCount}${alertsHasMore ? "+" : ""})`;
  if (t === "map") return "Carte";
  if (t === "planning") return "Stratégie";
  if (t === "areas") return "Zones";
  if (t === "broadcasts") return "Diffusion";
  if (t === "agencies") return "Agences";
  if (t === "audit") return "Audit";
  if (t === "settings") return "Paramètres";
  if (t === "system") return "Système";
  return t;
};

// Map a tab key to its URL. `dashboard` lives at `/admin` to match the
// existing default route; everything else lives at `/admin/{tab}`.
export const tabHref = (t) => (t === "dashboard" ? "/admin" : `/admin/${t}`);

/**
 * Horizontal tab strip used at the top of the Admin Command Center.
 *
 * Each tab is now a real `<Link>` to its own URL, so:
 *   - Right-click → "Open in new tab" / "Open in new window" works natively
 *   - Cmd/Ctrl+Click opens the tab in a new browser tab
 *   - Middle-click opens the tab in a new browser tab
 *   - The URL is bookmarkable + shareable
 *
 * The thin "↗" badge on the active tab is a quick affordance for opening
 * the current screen in a separate window without right-clicking.
 *
 * Phase 2C (spec §8.2) — `guardTab`/`onGuardedNavigate`: when the admin
 * is currently ON `guardTab` (i.e. "planning") with unsaved Planification
 * work, a plain-click navigation to a DIFFERENT tab is intercepted here
 * (`preventDefault`) and handed to the parent, which shows the confirm
 * dialog and performs the real navigation on "Quitter et abandonner".
 * Cmd/Ctrl/middle-click are left alone on purpose — those open a NEW
 * browser tab/window, so Planification's current tab (and its unsaved
 * work) is never actually left.
 */
export const AdminTabsBar = ({
  tab,
  usersCount,
  usersHasMore,
  alertsCount,
  alertsHasMore,
  guardTab,
  onGuardedNavigate,
}) => (
  // Iter77 — wrap the horizontal scroll row so a right-side fade
  // gradient can hint at overflow. Without this hint, users mistake
  // the truncated "Nouvelle fenêtre" button for a broken layout.
  <div className="relative">
    <div
      className="flex border-b border-zinc-800 bg-[#0b0f14] overflow-x-auto cmd-scroll-row"
      role="tablist"
    >
    {ADMIN_TABS.map((t) => {
      const Icon = tabIcon(t);
      const active = tab === t;
      const href = tabHref(t);
      return (
        <Link
          key={t}
          to={href}
          data-testid={`admin-tab-${t}`}
          role="tab"
          aria-selected={active}
          onClick={(e) => {
            if (!guardTab || tab !== guardTab || t === tab) return;
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; // new-tab/new-window clicks never leave this one
            e.preventDefault();
            onGuardedNavigate?.(href);
          }}
          className={`relative shrink-0 px-3.5 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors flex items-center gap-1.5 ${
            active ? "text-emerald-300" : "text-zinc-400 hover:text-zinc-100"
          }`}
        >
          {Icon && <Icon className={`w-3.5 h-3.5 ${active ? "text-emerald-400" : "text-zinc-500"}`} />}
          {tabLabel(t, usersCount, usersHasMore, alertsCount, alertsHasMore)}
          {active && (
            <span
              className="absolute left-3 right-3 -bottom-px h-[2px] bg-emerald-400"
              aria-hidden="true"
            />
          )}
        </Link>
      );
    })}
    {/* Quick "open current view in a new window" shortcut. Visible only
        when an actual tab is active. Uses window.open with a unique
        target so each click spawns a new chrome window rather than
        reusing one. */}
    <button
      type="button"
      data-testid="admin-tab-open-new-window"
      onClick={() => {
        try {
          window.open(tabHref(tab), `_blank_${tab}_${Date.now()}`, "noopener,noreferrer");
        } catch (_e) { /* noop */ }
      }}
      title="Ouvrir cet écran dans une nouvelle fenêtre"
      aria-label="Ouvrir cet écran dans une nouvelle fenêtre"
      className="shrink-0 ml-auto px-3 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500 hover:text-emerald-300 flex items-center gap-1.5"
    >
      <ExternalLink className="w-3.5 h-3.5" />
      Nouvelle fenêtre
    </button>
    </div>
    {/* Right-side scroll fade — pure CSS gradient, non-interactive.
        Signals to the operator that the tab row can be scrolled if
        it visually clips at the viewport edge. */}
    <div
      aria-hidden
      className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-[#0b0f14] to-transparent"
    />
  </div>
);
